import type { PhotoId } from '../gallery/photos';
import type { TextLayer } from '../template';
import { emph, lin, money, rad, solid, step, t, type Box, type Tx } from './kit';
import { SN, type VideoTemplateDef } from './types';

/** Fast beat cuts on photos with punch-ins and one-word captions (product drop). */
const beatCut: VideoTemplateDef = {
  id: 'beat-cut-drop',
  category: 'product',
  format: 'vertical',
  name: t(
    'Beat-cut drop',
    'Дроп у ритмі біту',
    'Дроп в ритме бита',
    'Drop w rytmie beatu',
    'Drop pe ritm',
    'Drop al ritmo',
  ),
  description: t(
    'Fast beat cuts, punch-ins and one-word captions',
    'Швидкі склейки в біт, наїзди й підписи одним словом',
    'Быстрые склейки в бит, наезды и подписи одним словом',
    'Szybkie cięcia w rytm, najazdy i podpisy jednym słowem',
    'Tăieturi rapide pe ritm, zoom-uri și titluri dintr-un cuvânt',
    'Cortes rápidos al ritmo, zooms y rótulos de una palabra',
  ),
  scenes: [
    { key: 'hook', name: SN.hook, duration: 1500, transition: { preset: 'flash', duration: 300 } },
    {
      key: 'cut1',
      name: t('Cut 1', 'Кадр 1', 'Кадр 1', 'Ujęcie 1', 'Cadru 1', 'Plano 1'),
      duration: 700,
      transition: { preset: 'cut' },
    },
    {
      key: 'cut2',
      name: t('Cut 2', 'Кадр 2', 'Кадр 2', 'Ujęcie 2', 'Cadru 2', 'Plano 2'),
      duration: 700,
      transition: { preset: 'cut' },
    },
    {
      key: 'cut3',
      name: t('Cut 3', 'Кадр 3', 'Кадр 3', 'Ujęcie 3', 'Cadru 3', 'Plano 3'),
      duration: 700,
      transition: { preset: 'cut' },
    },
    {
      key: 'cut4',
      name: t('Cut 4', 'Кадр 4', 'Кадр 4', 'Ujęcie 4', 'Cadru 4', 'Plano 4'),
      duration: 700,
      transition: { preset: 'whip-pan', duration: 340, params: { direction: 'left' } },
    },
    {
      key: 'product',
      name: SN.product,
      duration: 2800,
      transition: { preset: 'zoom-in', duration: 500 },
    },
    { key: 'cta', name: SN.cta, duration: 2400 },
  ],
  build(b) {
    const C = { ink: '#0B0B0F', volt: '#D7FF3A', white: '#FFFFFF' };
    const head: Partial<TextLayer> = {
      fontFamily: 'Oswald',
      fontWeight: 700,
      textTransform: 'uppercase',
      lineHeight: 0.95,
    };
    const F = b.F;

    const hook = b.sc('hook');
    b.photo(
      hook,
      'photo_hook',
      'leather-shoes',
      b.full(),
      { brightness: 0.72 },
      { keys: b.punchIn(1.32, 1, 1.06, 380) },
    );
    b.shade(hook, 'shade_hook', b.full(), [
      [0, '#0B0B0F00'],
      [0.5, '#0B0B0F40'],
      [1, '#0B0B0FD9'],
    ]);
    b.text(
      hook,
      'headline',
      t('NEW DROP', 'НОВИЙ ДРОП', 'НОВЫЙ ДРОП', 'NOWY DROP', 'DROP NOU', 'NUEVO DROP'),
      b.in(0, 0.3, 1, 0.34),
      { ...head, fontSize: b.s(0.24), color: C.white },
      { enter: step('zoom-out', 0, 380, 'out-expo') },
    );
    b.chip(
      hook,
      'tag',
      t(
        'SS26 · Limited',
        'SS26 · Лімітовано',
        'SS26 · Лимитировано',
        'SS26 · Limitowana',
        'SS26 · Ediție limitată',
        'SS26 · Edición limitada',
      ),
      { x: b.cx, y: F.y + F.h * 0.68, h: b.s(0.09), align: 'center' },
      { bg: C.volt, color: C.ink, font: 'Oswald', weight: 600, upper: true, k: 0.55 },
      { enter: step('wipe', 300, 320, 'out-expo', { direction: 'right' }) },
    );

    const cuts: Array<{ key: string; photo: PhotoId; word: Tx; y: number; volt: boolean }> = [
      {
        key: 'cut1',
        photo: 'fashion-model',
        word: t('BOLD', 'СМІЛИВО', 'СМЕЛО', 'ODWAŻNIE', 'ÎNDRĂZNEȚ', 'ATREVIDO'),
        y: 0.04,
        volt: false,
      },
      {
        key: 'cut2',
        photo: 'retro-style',
        word: t('FRESH', 'СВІЖО', 'СВЕЖО', 'ŚWIEŻO', 'PROASPĂT', 'FRESCO'),
        y: 0.72,
        volt: true,
      },
      {
        key: 'cut3',
        photo: 'fashion-selfie',
        word: t('LOUD', 'ЯСКРАВО', 'ЯРКО', 'GŁOŚNO', 'VIBRANT', 'INTENSO'),
        y: 0.4,
        volt: false,
      },
      {
        key: 'cut4',
        photo: 'leather-shoes',
        word: t('YOURS', 'ТВОЄ', 'ТВОЁ', 'TWOJE', 'AL TĂU', 'TUYO'),
        y: 0.72,
        volt: true,
      },
    ];
    cuts.forEach((c, i) => {
      const sc = b.sc(c.key);
      b.photo(
        sc,
        `photo_${i + 1}`,
        c.photo,
        b.full(),
        i === 3 ? { focusX: 0.3, flipX: true } : {},
        { keys: b.punchIn(1.24, 1.02, 1.08, 220) },
      );
      b.text(
        sc,
        `word_${i + 1}`,
        c.word,
        b.in(0, c.y, 1, 0.2),
        c.volt
          ? {
              ...head,
              fontSize: b.s(0.2),
              color: C.ink,
              highlight: { color: C.volt, paddingX: b.s(0.03), paddingY: 0, radius: 0 },
            }
          : {
              ...head,
              fontSize: b.s(0.2),
              color: C.white,
              shadow: { x: 0, y: b.s(0.006), blur: b.s(0.03), color: '#00000099' },
            },
        { enter: step('zoom-out', 0, 240, 'out-expo') },
      );
      b.text(sc, `counter_${i + 1}`, `0${i + 1} / 04`, b.in(0, 0.94, 0.4, 0.06), {
        fontFamily: 'Oswald',
        fontWeight: 500,
        fontSize: b.s(0.036),
        color: C.white,
        align: 'left',
        letterSpacing: b.s(0.004),
        shadow: { x: 0, y: 2, blur: 8, color: '#00000080' },
      });
      if (i > 0) b.addCamera(sc, 'punch', 0, 380, { intensity: 1.4 });
    });

    const pr = b.sc('product');
    b.backdrop(pr, 'product_bg', solid(C.volt));
    b.text(
      pr,
      'product_bg_word',
      t('NEW', 'НОВЕ', 'НОВОЕ', 'NOWE', 'NOU', 'NUEVO'),
      { x: F.x, y: F.y, w: F.w, h: F.h * 0.3 },
      {
        ...head,
        fontSize: b.s(0.42),
        color: 'transparent',
        strokeWidth: Math.max(2, b.s(0.004)),
        strokeColor: C.ink,
        opacity: 0.35,
      },
      { enter: step('slide', 0, 2400, 'out-quart', { direction: 'left', distance: 20 }) },
    );
    const side = !b.upright;
    const card = side ? b.in(0.02, 0.1, 0.46, 0.8) : b.in(0.1, 0.2, 0.8, 0.46);
    const d = b.s(0.022);
    b.shape(
      pr,
      'card_shadow',
      { ...card, x: card.x + d, y: card.y + d },
      { fill: solid(C.ink), borderRadius: b.s(0.03) },
      { enter: step('pop', 120, 500, 'out-back') },
    );
    b.photo(
      pr,
      'product_photo',
      'leather-shoes',
      card,
      { borderRadius: b.s(0.03), borderWidth: b.s(0.008), borderColor: C.ink },
      {
        enter: step('pop', 0, 500, 'out-back'),
        keys: [
          { at: 0, scale: 1 },
          { at: -1, scale: 1.04 },
        ],
      },
    );
    const txt = side ? b.in(0.54, 0.18, 0.46, 0.64) : b.in(0, 0.7, 1, 0.3);
    const align = side ? 'left' : 'center';
    b.text(
      pr,
      'product_name',
      'Oxford 02',
      { x: txt.x, y: txt.y, w: txt.w, h: txt.h * (side ? 0.3 : 0.36) },
      { ...head, fontSize: b.s(0.13), color: C.ink, align },
      { enter: step('rise', 350, 500, 'out-expo') },
    );
    b.text(
      pr,
      'product_desc',
      t(
        'Hand-stitched leather · 4 colours',
        'Шкіра ручної роботи · 4 кольори',
        'Кожа ручной работы · 4 цвета',
        'Ręcznie szyta skóra · 4 kolory',
        'Piele cusută manual · 4 culori',
        'Piel cosida a mano · 4 colores',
      ),
      { x: txt.x, y: txt.y + txt.h * (side ? 0.32 : 0.36), w: txt.w, h: txt.h * 0.18 },
      { fontFamily: 'Inter', fontWeight: 600, fontSize: b.s(0.042), color: C.ink, align },
      { enter: step('fade', 550, 400) },
    );
    const price = b.chip(
      pr,
      'price',
      money({ usd: 89, uah: 3499, rub: 7990, pln: 349, ron: 449, eur: 85 }),
      {
        x: side ? txt.x : b.cx,
        y: txt.y + txt.h * (side ? 0.58 : 0.6),
        h: b.s(0.1),
        align: side ? 'left' : 'center',
      },
      { bg: C.ink, color: C.volt, font: 'Oswald', weight: 700, k: 0.55 },
      {
        enter: step('pop', 800, 450, 'spring-bouncy'),
        emphasis: emph('heartbeat', 1500, 900),
      },
    );
    b.addFx(pr, 'burst', 900, 700, { anchor: price.id, color: C.ink, color2: '#FFFFFF' });

    const cta = b.sc('cta');
    b.backdrop(cta, 'cta_bg', solid(C.ink));
    const tile = Math.min(b.s(0.22), F.w * 0.3);
    const tiles: PhotoId[] = ['fashion-model', 'leather-shoes', 'retro-style'];
    tiles.forEach((p, i) =>
      b.photo(
        cta,
        `thumb_${i + 1}`,
        p,
        {
          x: b.cx + (i - 1) * (tile + b.s(0.025)) - tile / 2,
          y: F.y + F.h * (b.tall ? 0.16 : 0.08),
          w: tile,
          h: tile,
        },
        { borderRadius: b.s(0.025), rotation: (i - 1) * 4 },
        { enter: step('pop', i * 110, 450, 'out-back') },
      ),
    );
    b.text(
      cta,
      'cta_headline',
      t('SHOP THE DROP', 'ХАПАЙ ДРОП', 'ЛОВИ ДРОП', 'ŁAP DROP', 'PRINDE DROP-UL', 'PILLA EL DROP'),
      b.in(0, b.tall ? 0.4 : 0.36, 1, 0.24),
      { ...head, fontSize: b.s(0.15), color: C.white },
      { enter: step('words-rise', 250, 500), split: { by: 'word', stagger: 90 } },
    );
    b.chip(
      cta,
      'cta',
      t('Shop now', 'Купити зараз', 'Купить сейчас', 'Kup teraz', 'Cumpără acum', 'Compra ya'),
      { x: b.cx, y: F.y + F.h * (b.tall ? 0.68 : 0.66), h: b.s(0.13), align: 'center' },
      { bg: C.volt, color: C.ink, font: 'Oswald', weight: 700, upper: true, k: 0.55 },
      { enter: step('pop', 650, 450, 'out-back'), emphasis: emph('heartbeat', 1300, 900, 0, 300) },
    );
    b.text(
      cta,
      'site',
      'yourbrand.com',
      b.in(0, b.tall ? 0.84 : 0.86, 1, 0.07),
      { fontFamily: 'Inter', fontWeight: 600, fontSize: b.s(0.04), color: '#FFFFFFB3' },
      { enter: step('fade', 900, 400) },
    );
    return solid(C.ink);
  },
};

/** Word-by-word giant type, colour flashes and hard cuts (sale). */
const kineticSale: VideoTemplateDef = {
  id: 'kinetic-type-sale',
  category: 'sale',
  format: 'vertical',
  name: t(
    'Kinetic sale',
    'Кінетичний розпродаж',
    'Кинетическая распродажа',
    'Kinetyczna wyprzedaż',
    'Reduceri kinetice',
    'Rebajas cinéticas',
  ),
  description: t(
    'Giant word-by-word type, colour flashes, hard cuts',
    'Великі слова одне за одним, кольорові спалахи, різкі склейки',
    'Огромные слова одно за другим, цветные вспышки, резкие склейки',
    'Wielkie słowa jedno po drugim, kolorowe błyski, ostre cięcia',
    'Cuvinte uriașe pe rând, flash-uri de culoare, tăieturi bruște',
    'Palabras gigantes una a una, destellos de color, cortes secos',
  ),
  scenes: [
    { key: 'mega', name: SN.hook, duration: 1000, transition: { preset: 'cut' } },
    {
      key: 'sale',
      name: t('Sale', 'Розпродаж', 'Распродажа', 'Wyprzedaż', 'Reduceri', 'Rebajas'),
      duration: 1000,
      transition: { preset: 'flash', duration: 300 },
    },
    {
      key: 'discount',
      name: t('Discount', 'Знижка', 'Скидка', 'Rabat', 'Reducere', 'Descuento'),
      duration: 1800,
      transition: {
        preset: 'panels',
        duration: 800,
        params: { direction: 'up', color: '#FFE600', color2: '#FF2E2E', color3: '#111111' },
      },
    },
    {
      key: 'words',
      name: t('Deadline', 'Термін', 'Срок', 'Termin', 'Termen', 'Plazo'),
      duration: 2400,
      transition: { preset: 'push', duration: 450, params: { direction: 'up' } },
    },
    {
      key: 'products',
      name: t('Deals', 'Товари', 'Товары', 'Okazje', 'Oferte', 'Ofertas'),
      duration: 2800,
      transition: {
        preset: 'stripes',
        duration: 900,
        params: { color: '#FF2E2E', color2: '#FFE600', color3: '#111111' },
      },
    },
    { key: 'cta', name: SN.cta, duration: 2400 },
  ],
  build(b) {
    const C = { yellow: '#FFE600', red: '#FF2E2E', ink: '#111111', white: '#FFFFFF' };
    const big: Partial<TextLayer> = {
      fontFamily: 'Unbounded',
      fontWeight: 900,
      textTransform: 'uppercase',
      lineHeight: 0.95,
      letterSpacing: -b.s(0.003),
    };
    const F = b.F;

    const mega = b.sc('mega');
    b.backdrop(mega, 'mega_bg', solid(C.yellow));
    [0.16, 0.82].forEach((y, i) =>
      b.shape(
        mega,
        `mega_bar_${i + 1}`,
        { x: 0, y: F.y + F.h * y, w: b.W, h: b.s(0.02) },
        { fill: solid(C.ink) },
        { enter: step('wipe', i * 90, 320, 'out-expo', { direction: i ? 'left' : 'right' }) },
      ),
    );
    b.text(
      mega,
      'mega',
      t('MEGA', 'МЕГА', 'МЕГА', 'MEGA', 'MEGA', 'MEGA'),
      b.in(0, 0.3, 1, 0.4),
      { ...big, fontSize: b.s(0.3), color: C.ink },
      { enter: step('letters-pop', 0, 420), split: { by: 'char', stagger: 60 } },
    );
    b.addCamera(mega, 'punch', 0, 400, { intensity: 1.4 });

    const sale = b.sc('sale');
    b.backdrop(sale, 'sale_bg', solid(C.red));
    b.text(
      sale,
      'sale',
      t('SALE', 'РОЗПРОДАЖ', 'РАСПРОДАЖА', 'WYPRZEDAŻ', 'REDUCERI', 'REBAJAS'),
      b.in(0, 0.3, 1, 0.4),
      { ...big, fontSize: b.s(0.3), color: C.white },
      { enter: step('stretch', 0, 380) },
    );
    [
      { x: 0.02, y: 0.06 },
      { x: 0.82, y: 0.78 },
    ].forEach((p, i) =>
      b.shape(
        sale,
        `sale_star_${i + 1}`,
        { ...b.in(p.x, p.y, 0, 0), w: b.s(0.13), h: b.s(0.13) },
        { shape: 'star', points: 8, innerRatio: 0.55, fill: solid(C.yellow) },
        { enter: step('spin', 0, 500), emphasis: emph('spin', 500, 3000, 0) },
      ),
    );
    b.addCamera(sale, 'shake', 0, 500, { intensity: 1.2 });

    const disc = b.sc('discount');
    b.backdrop(disc, 'discount_bg', rad('#2A2A2A', C.ink));
    const pct = b.text(
      disc,
      'discount',
      '−50%',
      b.in(0, 0.24, 1, 0.36),
      { ...big, fontSize: b.s(0.36), color: C.yellow },
      {
        enter: step('pop', 0, 500, 'spring-bouncy'),
        emphasis: emph('heartbeat', 900, 800),
      },
    );
    b.text(
      disc,
      'discount_note',
      t('on everything', 'на все', 'на всё', 'na wszystko', 'la tot', 'en todo'),
      b.in(0, 0.62, 1, 0.12),
      { ...big, fontWeight: 700, fontSize: b.s(0.08), color: C.white },
      { enter: step('words-rise', 350, 500), split: { by: 'word', stagger: 80 } },
    );
    b.addFx(disc, 'burst', 100, 800, { anchor: pct.id, color: C.yellow, color2: C.red, size: 1.4 });
    b.addCamera(disc, 'zoom-burst', 0, 600);

    const words = b.sc('words');
    b.backdrop(words, 'words_bg', solid(C.yellow));
    const lines: Tx[] = [
      t('ONLY', 'ТІЛЬКИ', 'ТОЛЬКО', 'TYLKO', 'DOAR', 'SOLO'),
      t('THIS', 'ЦІ', 'ЭТИ', 'TEN', 'ACEST', 'ESTE'),
      t('WEEKEND', 'ВИХІДНІ', 'ВЫХОДНЫЕ', 'WEEKEND', 'WEEKEND', 'FINDE'),
    ];
    lines.forEach((line, i) => {
      b.text(
        words,
        `line_${i + 1}`,
        line,
        b.in(0, 0.14 + i * 0.25, 1, 0.22),
        i === 1
          ? {
              ...big,
              fontSize: b.s(0.2),
              color: C.white,
              highlight: { color: C.red, paddingX: b.s(0.03), paddingY: 0, radius: b.s(0.01) },
            }
          : { ...big, fontSize: b.s(0.2), color: C.ink },
        {
          enter: step('slide', i * 240, 420, 'out-expo', {
            direction: i % 2 ? 'right' : 'left',
            distance: 140,
          }),
          ...(i === 2 ? { emphasis: emph('pulse', 1200, 1000, 0) } : {}),
        },
      );
      b.addCamera(words, 'punch', i * 240 + 120, 320, { intensity: 0.8 });
    });

    const prod = b.sc('products');
    b.backdrop(prod, 'products_bg', solid(C.white));
    b.text(
      prod,
      'deals_title',
      t(
        'HOT DEALS',
        'ГАРЯЧІ ЗНИЖКИ',
        'ГОРЯЧИЕ СКИДКИ',
        'GORĄCE OKAZJE',
        'OFERTE TARI',
        'OFERTAS TOP',
      ),
      b.in(0, 0, 1, 0.12),
      { ...big, fontSize: b.s(0.08), color: C.ink },
      { enter: step('words-rise', 0, 450), split: { by: 'word', stagger: 70 } },
    );
    const items: Array<{ photo: PhotoId; tag: string }> = [
      { photo: 'leather-shoes', tag: '−50%' },
      { photo: 'fashion-model', tag: '−30%' },
      { photo: 'lipstick', tag: '−40%' },
    ];
    const gap = b.s(0.025);
    items.forEach((it, i) => {
      const area = b.in(0, 0.15, 1, 0.85);
      const box: Box = b.upright
        ? { x: area.x, y: area.y + i * ((area.h + gap) / 3), w: area.w, h: (area.h - 2 * gap) / 3 }
        : { x: area.x + i * ((area.w + gap) / 3), y: area.y, w: (area.w - 2 * gap) / 3, h: area.h };
      b.photo(
        prod,
        `deal_${i + 1}`,
        it.photo,
        box,
        { borderRadius: b.s(0.03) },
        { enter: step('pop', 150 + i * 140, 450, 'out-back') },
      );
      b.chip(
        prod,
        `deal_tag_${i + 1}`,
        it.tag,
        { x: box.x + b.s(0.02), y: box.y + b.s(0.02), h: b.s(0.09) },
        {
          bg: C.red,
          color: C.white,
          font: 'Unbounded',
          weight: 900,
          k: 0.75,
          radius: b.s(0.015),
          extra: { rotation: -6 },
        },
        {
          enter: step('pop', 450 + i * 140, 350, 'spring-bouncy'),
          emphasis: emph('tada', 1300 + i * 200, 800),
        },
      );
    });

    const cta = b.sc('cta');
    b.backdrop(cta, 'cta_bg', solid(C.red));
    b.text(
      cta,
      'cta',
      t('SHOP NOW', 'КУПУЙ ЗАРАЗ', 'ПОКУПАЙ', 'KUPUJ TERAZ', 'CUMPĂRĂ ACUM', 'COMPRA YA'),
      b.in(0, 0.2, 1, 0.34),
      { ...big, fontSize: b.s(0.17), color: C.white },
      {
        enter: step('zoom-out', 0, 450, 'out-expo'),
        emphasis: emph('heartbeat', 900, 900, 0, 400),
      },
    );
    b.chip(
      cta,
      'dates',
      t('FRI – SUN', 'ПТ – НД', 'ПТ – ВС', 'PT – ND', 'VIN – DUM', 'VIE – DOM'),
      { x: b.cx, y: F.y + F.h * 0.6, h: b.s(0.11), align: 'center' },
      { bg: C.yellow, color: C.ink, font: 'Unbounded', weight: 900, k: 0.75 },
      { enter: step('pop', 400, 400, 'out-back') },
    );
    b.text(
      cta,
      'site',
      'yourshop.com',
      b.in(0, 0.8, 1, 0.08),
      { fontFamily: 'Unbounded', fontWeight: 600, fontSize: b.s(0.045), color: C.white },
      { enter: step('fade', 700, 400) },
    );
    return solid(C.ink);
  },
};

/** UGC review: phone-shot photos, TikTok-style captions, star rating card. */
const ugcReview: VideoTemplateDef = {
  id: 'ugc-review',
  category: 'social',
  format: 'vertical',
  name: t('UGC review', 'UGC-відгук', 'UGC-отзыв', 'Recenzja UGC', 'Recenzie UGC', 'Reseña UGC'),
  description: t(
    'Creator-style captions, star rating and a promo code',
    'Субтитри як у блогера, зірковий рейтинг і промокод',
    'Субтитры как у блогера, звёздный рейтинг и промокод',
    'Napisy jak u twórcy, ocena w gwiazdkach i kod rabatowy',
    'Subtitrări de creator, rating cu stele și cod promoțional',
    'Subtítulos de creador, valoración con estrellas y código',
  ),
  scenes: [
    {
      key: 'hook',
      name: SN.hook,
      duration: 2800,
      transition: { preset: 'push', duration: 400, params: { direction: 'up' } },
    },
    {
      key: 'taste',
      name: t('Story', 'Історія', 'История', 'Historia', 'Poveste', 'Historia'),
      duration: 2800,
      transition: { preset: 'cut' },
    },
    {
      key: 'proof',
      name: t('Rating', 'Оцінка', 'Оценка', 'Ocena', 'Rating', 'Valoración'),
      duration: 3200,
      transition: { preset: 'slide-over', duration: 450, params: { direction: 'left' } },
    },
    {
      key: 'offer',
      name: SN.offer,
      duration: 2600,
      transition: { preset: 'zoom-in', duration: 450 },
    },
    { key: 'cta', name: SN.cta, duration: 2400 },
  ],
  build(b) {
    const C = {
      cream: '#F5EBDD',
      brown: '#3B2416',
      orange: '#FF7A2F',
      ink: '#0E0E0E',
      white: '#FFFFFF',
    };
    const F = b.F;
    const cap = (bg: string, fg: string): Partial<TextLayer> => ({
      fontFamily: 'Montserrat',
      fontWeight: 800,
      fontSize: b.s(0.066),
      color: fg,
      lineHeight: 1.36,
      highlight: { color: bg, paddingX: b.s(0.022), paddingY: b.s(0.006), radius: b.s(0.014) },
    });

    const hook = b.sc('hook');
    b.photo(
      hook,
      'photo_hook',
      'remote-work',
      b.full(),
      {},
      { keys: b.kenBurns(1.04, 1.14, -0.01) },
    );
    b.shade(hook, 'shade_hook', b.full(), [
      [0, '#00000066'],
      [0.25, '#00000000'],
      [0.7, '#00000000'],
      [1, '#00000080'],
    ]);
    const av = b.s(0.105);
    b.photo(
      hook,
      'avatar',
      'remote-work',
      { x: F.x, y: F.y, w: av, h: av },
      { borderRadius: av, borderWidth: b.s(0.005), borderColor: C.white },
      { enter: step('pop', 0, 400, 'out-back') },
    );
    b.text(
      hook,
      'handle',
      '@alex.brews',
      { x: F.x + av + b.s(0.02), y: F.y, w: F.w * 0.5, h: av },
      {
        fontFamily: 'Montserrat',
        fontWeight: 700,
        fontSize: b.s(0.046),
        align: 'left',
        shadow: { x: 0, y: 2, blur: 10, color: '#00000099' },
      },
      { enter: step('fade', 150, 400) },
    );
    b.chip(
      hook,
      'review_tag',
      t('Review', 'Відгук', 'Отзыв', 'Recenzja', 'Recenzie', 'Reseña'),
      { x: F.x + F.w, y: F.y + b.s(0.015), h: b.s(0.06), align: 'right' },
      { bg: C.orange, color: C.white, font: 'Montserrat', weight: 800, upper: true },
      { enter: step('pop', 250, 400, 'out-back') },
    );
    b.text(
      hook,
      'caption_1',
      t(
        'I tried this coffee for 30 days…',
        'Я пив цю каву 30 днів…',
        'Я пил этот кофе 30 дней…',
        'Piłem tę kawę przez 30 dni…',
        'Am băut cafeaua asta 30 de zile…',
        'Probé este café durante 30 días…',
      ),
      b.in(0.02, 0.52, 0.96, 0.3),
      cap(C.white, C.ink),
      { enter: step('pop', 300, 300, 'out-back'), split: { by: 'word', stagger: 130 } },
    );

    const taste = b.sc('taste');
    b.photo(taste, 'photo_story', 'coffee-beans', b.full(), {}, { keys: b.kenBurns(1.14, 1.02) });
    b.text(
      taste,
      'caption_2',
      t(
        'No more bitter office coffee',
        'Більше ніякої гіркої кави в офісі',
        'Больше никакого горького кофе в офисе',
        'Koniec z gorzką kawą w biurze',
        'Gata cu cafeaua amară de la birou',
        'Adiós al café amargo de oficina',
      ),
      b.in(0.02, 0.14, 0.96, 0.3),
      cap(C.ink, C.white),
      { enter: step('pop', 0, 300, 'out-back'), split: { by: 'word', stagger: 110 } },
    );
    b.text(
      taste,
      'caption_3',
      t(
        'Smooth. Rich. Fresh every week.',
        'М’яка. Насичена. Свіжа щотижня.',
        'Мягкий. Насыщенный. Свежий каждую неделю.',
        'Łagodna. Wyrazista. Świeża co tydzień.',
        'Fină. Intensă. Proaspătă în fiecare săptămână.',
        'Suave. Intenso. Fresco cada semana.',
      ),
      b.in(0.02, 0.6, 0.96, 0.3),
      cap(C.orange, C.white),
      { enter: step('pop', 1200, 300, 'out-back'), split: { by: 'word', stagger: 110 } },
    );

    const proof = b.sc('proof');
    b.photo(
      proof,
      'photo_rating',
      'remote-work',
      b.full(),
      { brightness: 0.8, focusX: 0.4 },
      { keys: b.kenBurns(1.1, 1.18, 0.01) },
    );
    const card = b.pick<Box>({
      upright: b.in(0, 0.5, 1, 0.46),
      square: b.in(0.06, 0.42, 0.88, 0.56),
      wide: b.in(0.52, 0.1, 0.48, 0.8),
    });
    b.shape(
      proof,
      'review_card',
      card,
      {
        fill: solid(C.white),
        borderRadius: b.s(0.045),
        shadow: { x: 0, y: b.s(0.015), blur: b.s(0.05), color: '#00000055' },
      },
      { enter: step('rise', 0, 600, 'out-expo') },
    );
    const st = Math.min(card.w * 0.11, b.s(0.075));
    for (let i = 0; i < 5; i++)
      b.shape(
        proof,
        `star_${i + 1}`,
        {
          x: card.x + card.w / 2 + (i - 2) * st * 1.25 - st / 2,
          y: card.y + card.h * 0.1,
          w: st,
          h: st,
        },
        { shape: 'star', points: 5, innerRatio: 0.45, fill: solid(C.orange) },
        { enter: step('pop', 350 + i * 90, 380, 'spring-bouncy') },
      );
    const pad = card.w * 0.08;
    b.text(
      proof,
      'quote',
      t(
        '“Smooth, rich and zero bitterness. I’m hooked.”',
        '«М’яка, насичена й зовсім не гірка. Я в захваті.»',
        '«Мягкий, насыщенный и совсем не горький. Я в восторге.»',
        '„Łagodna, wyrazista i zero goryczy. Przepadłem.”',
        '„Fină, intensă și deloc amară. M-a cucerit.”',
        '«Suave, intenso y nada amargo. Me encanta.»',
      ),
      { x: card.x + pad, y: card.y + card.h * 0.27, w: card.w - pad * 2, h: card.h * 0.45 },
      {
        fontFamily: 'Montserrat',
        fontWeight: 700,
        fontSize: b.s(0.055),
        color: C.ink,
        lineHeight: 1.2,
      },
      { enter: step('fade', 800, 500) },
    );
    const ck = b.s(0.045);
    const authorY = card.y + card.h * 0.78;
    b.icon(
      proof,
      'verified_icon',
      'circle-check',
      { x: card.x + pad, y: authorY + (card.h * 0.12 - ck) / 2, w: ck, h: ck },
      C.orange,
      { enter: step('pop', 1100, 350, 'out-back') },
    );
    b.text(
      proof,
      'author',
      t(
        'Alex · verified buyer',
        'Олекс · перевірений покупець',
        'Алекс · проверенный покупатель',
        'Alex · zweryfikowany klient',
        'Alex · client verificat',
        'Alex · comprador verificado',
      ),
      { x: card.x + pad + ck * 1.3, y: authorY, w: card.w - pad * 2 - ck * 1.3, h: card.h * 0.12 },
      {
        fontFamily: 'Montserrat',
        fontWeight: 600,
        fontSize: b.s(0.036),
        color: '#55555A',
        align: 'left',
      },
      { enter: step('fade', 1150, 400) },
    );

    const offer = b.sc('offer');
    b.backdrop(offer, 'offer_bg', lin(160, C.brown, '#1A0F08'));
    const ring = b.pick({
      tall: F.w * 0.62,
      portrait: F.h * 0.4,
      square: F.h * 0.42,
      wide: F.h * 0.62,
    });
    const ringC = b.upright
      ? { x: b.cx, y: F.y + F.h * (b.tall ? 0.26 : 0.24) }
      : b.wide
        ? { x: F.x + F.w * 0.27, y: b.cy }
        : { x: F.x + F.w * 0.27, y: F.y + F.h * 0.3 };
    b.shape(
      offer,
      'offer_ring',
      b.centered(ringC.x, ringC.y, ring * 1.12, ring * 1.12),
      {
        shape: 'ellipse',
        fill: null,
        strokeWidth: Math.max(3, b.s(0.006)),
        strokeColor: C.orange,
        strokeStyle: 'dashed',
      },
      { enter: step('zoom-in', 100, 600), emphasis: emph('spin', 600, 12000, 1) },
    );
    b.photo(
      offer,
      'offer_photo',
      'coffee-beans',
      b.centered(ringC.x, ringC.y, ring, ring),
      { borderRadius: ring },
      { enter: step('pop', 0, 550, 'out-back') },
    );
    const ot: Box = b.upright
      ? b.in(0, b.tall ? 0.54 : 0.5, 1, 0.46)
      : b.wide
        ? b.in(0.55, 0.1, 0.45, 0.8)
        : b.in(0, 0.62, 1, 0.38);
    const sideAlign = b.upright || !b.wide ? 'center' : 'left';
    b.text(
      offer,
      'offer_title',
      t(
        'Your first box',
        'Перша коробка',
        'Первая коробка',
        'Pierwsze pudełko',
        'Prima cutie',
        'Tu primera caja',
      ),
      { x: ot.x, y: ot.y, w: ot.w, h: ot.h * 0.22 },
      {
        fontFamily: 'Montserrat',
        fontWeight: 800,
        fontSize: b.s(0.07),
        color: C.cream,
        align: sideAlign,
      },
      { enter: step('rise', 200, 450, 'out-expo') },
    );
    b.text(
      offer,
      'discount',
      '−40%',
      { x: ot.x, y: ot.y + ot.h * 0.22, w: ot.w, h: ot.h * 0.42 },
      {
        fontFamily: 'Montserrat',
        fontWeight: 900,
        fontSize: b.s(0.2),
        color: C.orange,
        align: sideAlign,
      },
      { enter: step('pop', 450, 500, 'spring-bouncy'), emphasis: emph('heartbeat', 1200, 900) },
    );
    b.chip(
      offer,
      'promo_code',
      t('code ALEX40', 'код ALEX40', 'код ALEX40', 'kod ALEX40', 'cod ALEX40', 'código ALEX40'),
      {
        x: sideAlign === 'left' ? ot.x : ot.x + ot.w / 2,
        y: ot.y + ot.h * 0.7,
        h: b.s(0.085),
        align: sideAlign,
      },
      {
        bg: '#FFFFFF1F',
        color: C.cream,
        font: 'Montserrat',
        weight: 700,
        radius: b.s(0.02),
        extra: { letterSpacing: b.s(0.003) },
      },
      { enter: step('wipe', 800, 450, 'out-expo', { direction: 'right' }) },
    );

    const cta = b.sc('cta');
    b.backdrop(cta, 'cta_bg', solid(C.cream));
    const ic = b.s(0.12);
    b.icon(
      cta,
      'cta_icon',
      'coffee',
      b.centered(b.cx, F.y + F.h * (b.tall ? 0.24 : 0.16), ic, ic),
      C.orange,
      { enter: step('drop', 0, 600, 'bounce'), emphasis: emph('wiggle', 900, 700) },
    );
    b.text(
      cta,
      'cta_headline',
      t(
        'Try it yourself',
        'Спробуй сам',
        'Попробуй сам',
        'Sprawdź sam',
        'Încearcă și tu',
        'Pruébalo tú',
      ),
      b.in(0, b.tall ? 0.34 : 0.28, 1, 0.22),
      { fontFamily: 'Montserrat', fontWeight: 900, fontSize: b.s(0.11), color: C.brown },
      { enter: step('words-rise', 150, 500), split: { by: 'word', stagger: 90 } },
    );
    b.chip(
      cta,
      'cta',
      t(
        'Get your first box',
        'Замовити першу коробку',
        'Заказать первую коробку',
        'Zamów pierwsze pudełko',
        'Comandă prima cutie',
        'Pide tu primera caja',
      ),
      { x: b.cx, y: F.y + F.h * (b.tall ? 0.62 : 0.6), h: b.s(0.11), align: 'center' },
      { bg: C.orange, color: C.white, font: 'Montserrat', weight: 800 },
      { enter: step('pop', 600, 450, 'out-back'), emphasis: emph('pulse', 1200, 1100, 0) },
    );
    b.text(
      cta,
      'site',
      'brewbox.com',
      b.in(0, b.tall ? 0.78 : 0.8, 1, 0.08),
      {
        fontFamily: 'Montserrat',
        fontWeight: 600,
        fontSize: b.s(0.042),
        color: C.brown,
        opacity: 0.75,
      },
      { enter: step('fade', 900, 400) },
    );
    return solid(C.ink);
  },
};

/** Before/after: the same room greyed out, wiped to full colour, then side by side. */
const beforeAfter: VideoTemplateDef = {
  id: 'before-after-wipe',
  category: 'realestate',
  format: 'portrait',
  name: t(
    'Before / after',
    'До / після',
    'До / после',
    'Przed / po',
    'Înainte / după',
    'Antes / después',
  ),
  description: t(
    'Wipe reveal from grey “before” to vivid “after”, then split screen',
    'Шторка від сірого «до» до яскравого «після», потім спліт-екран',
    'Шторка от серого «до» к яркому «после», затем сплит-экран',
    'Kurtyna z szarego „przed” w żywe „po”, potem podzielony ekran',
    'Cortină de la „înainte” gri la „după” viu, apoi ecran împărțit',
    'Cortinilla del «antes» gris al «después» vivo y pantalla dividida',
  ),
  scenes: [
    {
      key: 'before',
      name: t('Before', 'До', 'До', 'Przed', 'Înainte', 'Antes'),
      duration: 2400,
      transition: { preset: 'wipe', duration: 1000, params: { direction: 'right' } },
    },
    {
      key: 'after',
      name: t('After', 'Після', 'После', 'Po', 'După', 'Después'),
      duration: 2800,
      transition: { preset: 'crossfade', duration: 500 },
    },
    {
      key: 'compare',
      name: t('Side by side', 'Порівняння', 'Сравнение', 'Porównanie', 'Comparație', 'Comparación'),
      duration: 3000,
      transition: { preset: 'slide-over', duration: 600, params: { direction: 'up' } },
    },
    {
      key: 'services',
      name: t('Services', 'Послуги', 'Услуги', 'Usługi', 'Servicii', 'Servicios'),
      duration: 3000,
      transition: { preset: 'push', duration: 500, params: { direction: 'left' } },
    },
    { key: 'cta', name: SN.cta, duration: 2600 },
  ],
  build(b) {
    const C = { paper: '#F4F1EA', char: '#1F2421', terra: '#D9663B', white: '#FFFFFF' };
    const F = b.F;
    const sans: Partial<TextLayer> = { fontFamily: 'Manrope', fontWeight: 800 };
    const serif: Partial<TextLayer> = {
      fontFamily: 'Cormorant Garamond',
      fontWeight: 600,
      fontStyle: 'italic',
    };
    const before = { grayscale: true, brightness: 0.62, blur: 1 };
    const label = (
      scene: ReturnType<typeof b.sc>,
      name: string,
      text: Tx,
      at: { x: number; y: number; align?: 'left' | 'center' | 'right' },
      bg: string,
      delay: number,
    ) =>
      b.chip(
        scene,
        name,
        text,
        { ...at, h: b.s(0.08) },
        { bg, color: C.white, font: 'Manrope', weight: 800, upper: true, radius: b.s(0.012) },
        { enter: step('wipe', delay, 400, 'out-expo', { direction: 'right' }) },
      );
    const BEFORE = t('Before', 'До', 'До', 'Przed', 'Înainte', 'Antes');
    const AFTER = t('After', 'Після', 'После', 'Po', 'După', 'Después');

    const s1 = b.sc('before');
    b.photo(s1, 'photo_before', 'window-view', b.full(), before, {
      keys: b.kenBurns(1.04, 1.1),
    });
    b.shade(s1, 'shade_before', b.full(), [
      [0, '#00000000'],
      [0.35, '#00000000'],
      [0.6, '#00000080'],
      [1, '#000000D9'],
    ]);
    label(s1, 'label_before', BEFORE, { x: F.x, y: F.y }, C.char, 100);
    b.text(
      s1,
      'headline',
      t(
        'Same room.',
        'Та сама кімната.',
        'Та же комната.',
        'Ten sam pokój.',
        'Aceeași cameră.',
        'La misma sala.',
      ),
      b.in(0, 0.7, 1, 0.16),
      { ...sans, fontSize: b.s(0.1), color: C.white, align: 'left' },
      { enter: step('lines-mask', 300, 600), split: { by: 'line', stagger: 120 } },
    );
    b.text(
      s1,
      'subline',
      t(
        '14 days later →',
        'Через 14 днів →',
        'Через 14 дней →',
        '14 dni później →',
        'După 14 zile →',
        '14 días después →',
      ),
      b.in(0, 0.87, 1, 0.08),
      {
        fontFamily: 'Manrope',
        fontWeight: 600,
        fontSize: b.s(0.045),
        color: C.white,
        align: 'left',
        opacity: 0.85,
      },
      { enter: step('fade', 800, 400) },
    );

    const s2 = b.sc('after');
    const afterPhoto = b.photo(
      s2,
      'photo_after',
      'window-view',
      b.full(),
      { brightness: 1.18 },
      { keys: b.kenBurns(1.12, 1.02) },
    );
    b.shade(s2, 'shade_after', b.full(), [
      [0, '#00000000'],
      [0.35, '#00000000'],
      [0.6, '#1F242180'],
      [1, '#1F2421E6'],
    ]);
    label(s2, 'label_after', AFTER, { x: F.x, y: F.y }, C.terra, 300);
    b.text(
      s2,
      'after_headline',
      t(
        'Totally new.',
        'Зовсім нова.',
        'Совсем новая.',
        'Zupełnie nowy.',
        'Complet nouă.',
        'Totalmente nueva.',
      ),
      b.in(0, 0.7, 1, 0.2),
      { ...serif, fontSize: b.s(0.14), color: C.white, align: 'left' },
      { enter: step('letters-blur', 400, 700), split: { by: 'char', stagger: 25, order: 'start' } },
    );
    b.addFx(s2, 'sweep', 500, 1100, { target: afterPhoto.id, opacity: 0.35 });

    const s3 = b.sc('compare');
    const half = b.W / 2;
    b.photo(
      s3,
      'compare_before',
      'window-view',
      { x: 0, y: 0, w: half, h: b.H },
      { ...before, focusX: 0.35 },
      {
        enter: step('slide', 0, 600, 'out-expo', { direction: 'right', distance: 30 }),
      },
    );
    b.photo(
      s3,
      'compare_after',
      'window-view',
      { x: half, y: 0, w: half, h: b.H },
      { brightness: 1.18, focusX: 0.65 },
      { enter: step('wipe', 200, 700, 'out-expo', { direction: 'right' }) },
    );
    const bar = Math.max(4, b.s(0.008));
    b.shape(
      s3,
      'divider',
      { x: half - bar / 2, y: 0, w: bar, h: b.H },
      { fill: solid(C.white) },
      { enter: step('unfold', 500, 500, 'out-expo', { direction: 'down' }) },
    );
    const hd = b.s(0.11);
    b.shape(
      s3,
      'handle',
      b.centered(half, b.cy, hd, hd),
      {
        shape: 'ellipse',
        fill: solid(C.white),
        shadow: { x: 0, y: 4, blur: 20, color: '#00000055' },
      },
      { enter: step('pop', 800, 400, 'out-back'), emphasis: emph('pulse', 1200, 1600, 0) },
    );
    b.icon(
      s3,
      'handle_icon',
      'chevrons-right',
      b.centered(half, b.cy, hd * 0.5, hd * 0.5),
      C.char,
      {
        enter: step('pop', 900, 400, 'out-back'),
      },
    );
    const ly = F.y + F.h - b.s(0.08);
    label(
      s3,
      'compare_label_before',
      BEFORE,
      { x: half - b.s(0.03), y: ly, align: 'right' },
      C.char,
      900,
    );
    label(s3, 'compare_label_after', AFTER, { x: half + b.s(0.03), y: ly }, C.terra, 1000);

    const s4 = b.sc('services');
    b.backdrop(s4, 'services_bg', solid(C.paper));
    const photoBox = b.pick<Box>({
      upright: { x: F.x, y: F.y, w: F.w, h: F.h * 0.4 },
      square: b.in(0, 0, 0.42, 1),
      wide: b.in(0, 0, 0.45, 1),
    });
    b.photo(
      s4,
      'services_photo',
      'cozy-house',
      photoBox,
      { borderRadius: b.s(0.03) },
      { enter: step('wipe', 0, 700, 'out-expo', { direction: 'down' }), keys: b.kenBurns(1, 1.06) },
    );
    const list = b.upright ? b.in(0, 0.45, 1, 0.55) : b.in(0.48, 0.04, 0.52, 0.92);
    b.text(
      s4,
      'services_title',
      t(
        'Turnkey renovation',
        'Ремонт під ключ',
        'Ремонт под ключ',
        'Remont pod klucz',
        'Renovare la cheie',
        'Reforma llave en mano',
      ),
      { x: list.x, y: list.y, w: list.w, h: list.h * 0.24 },
      { ...sans, fontSize: b.s(0.075), color: C.char, align: 'left' },
      { enter: step('rise', 200, 500, 'out-expo') },
    );
    const services: Tx[] = [
      t(
        'Design project in 5 days',
        'Дизайн-проєкт за 5 днів',
        'Дизайн-проект за 5 дней',
        'Projekt w 5 dni',
        'Proiect de design în 5 zile',
        'Proyecto de diseño en 5 días',
      ),
      t(
        'Fixed price, no surprises',
        'Фіксована ціна без сюрпризів',
        'Фиксированная цена без сюрпризов',
        'Stała cena, bez niespodzianek',
        'Preț fix, fără surprize',
        'Precio cerrado, sin sorpresas',
      ),
      t(
        'Furniture & decor included',
        'Меблі й декор включно',
        'Мебель и декор включены',
        'Meble i dekor w cenie',
        'Mobilier și decor incluse',
        'Muebles y decoración incluidos',
      ),
    ];
    const rowH = (list.h * 0.7) / 3;
    services.forEach((s, i) => {
      const y = list.y + list.h * 0.28 + i * rowH;
      const dot = Math.min(rowH * 0.62, b.s(0.075));
      b.shape(
        s4,
        `service_dot_${i + 1}`,
        { x: list.x, y: y + (rowH - dot) / 2, w: dot, h: dot },
        { shape: 'ellipse', fill: solid(C.terra) },
        { enter: step('pop', 400 + i * 160, 380, 'out-back') },
      );
      b.icon(
        s4,
        `service_icon_${i + 1}`,
        'check',
        b.centered(list.x + dot / 2, y + rowH / 2, dot * 0.55, dot * 0.55),
        C.white,
        { enter: step('pop', 450 + i * 160, 380, 'out-back') },
      );
      b.text(
        s4,
        `service_${i + 1}`,
        s,
        { x: list.x + dot * 1.35, y, w: list.w - dot * 1.35, h: rowH },
        {
          fontFamily: 'Manrope',
          fontWeight: 600,
          fontSize: b.s(0.045),
          color: C.char,
          align: 'left',
        },
        {
          enter: step('slide', 450 + i * 160, 450, 'out-expo', { direction: 'left', distance: 20 }),
        },
      );
    });

    const s5 = b.sc('cta');
    b.backdrop(s5, 'cta_bg', solid(C.char));
    b.text(
      s5,
      'cta_headline',
      t(
        'Your home, reimagined.',
        'Ваш дім у новому світлі.',
        'Ваш дом в новом свете.',
        'Twój dom na nowo.',
        'Casa ta, reinventată.',
        'Tu hogar, reinventado.',
      ),
      b.in(0, 0.22, 1, 0.3),
      { ...serif, fontSize: b.s(0.13), color: C.white, lineHeight: 1 },
      { enter: step('lines-mask', 0, 700), split: { by: 'line', stagger: 140 } },
    );
    b.chip(
      s5,
      'cta',
      t(
        'Book a free consultation',
        'Безкоштовна консультація',
        'Бесплатная консультация',
        'Bezpłatna konsultacja',
        'Consultanță gratuită',
        'Consulta gratuita',
      ),
      { x: b.cx, y: F.y + F.h * 0.6, h: b.s(0.11), align: 'center' },
      { bg: C.terra, color: C.white, font: 'Manrope', weight: 800 },
      { enter: step('pop', 600, 450, 'out-back'), emphasis: emph('pulse', 1200, 1200, 0) },
    );
    b.text(
      s5,
      'site',
      'homestudio.com',
      b.in(0, 0.76, 1, 0.08),
      { fontFamily: 'Manrope', fontWeight: 600, fontSize: b.s(0.042), color: '#FFFFFFB3' },
      { enter: step('fade', 900, 400) },
    );
    return solid(C.char);
  },
};

/** Top-3 countdown list: big numbers, product cards, a winner with confetti. */
const top3: VideoTemplateDef = {
  id: 'top-3-countdown',
  category: 'product',
  format: 'vertical',
  name: t(
    'Top 3 countdown',
    'Топ-3 відлік',
    'Топ-3 обратный отсчёт',
    'Top 3 odliczanie',
    'Top 3 numărătoare',
    'Top 3 cuenta atrás',
  ),
  description: t(
    'List countdown: numbers drop in, cards stack, confetti for #1',
    'Відлік списку: цифри падають, картки складаються, конфеті для №1',
    'Обратный отсчёт: цифры падают, карточки складываются, конфетти для №1',
    'Odliczanie listy: spadające cyfry, karty, konfetti dla nr 1',
    'Numărătoare: cifre care cad, carduri, confetti pentru nr. 1',
    'Cuenta atrás: números que caen, tarjetas y confeti para el n.º 1',
  ),
  scenes: [
    {
      key: 'title',
      name: SN.intro,
      duration: 2200,
      transition: { preset: 'stack', duration: 600, params: { direction: 'up' } },
    },
    {
      key: 'n3',
      name: t('Number 3', 'Номер 3', 'Номер 3', 'Numer 3', 'Numărul 3', 'Número 3'),
      duration: 2600,
      transition: { preset: 'push', duration: 500, params: { direction: 'left' } },
    },
    {
      key: 'n2',
      name: t('Number 2', 'Номер 2', 'Номер 2', 'Numer 2', 'Numărul 2', 'Número 2'),
      duration: 2600,
      transition: { preset: 'push', duration: 500, params: { direction: 'left' } },
    },
    {
      key: 'n1',
      name: t('Number 1', 'Номер 1', 'Номер 1', 'Numer 1', 'Numărul 1', 'Número 1'),
      duration: 3000,
      transition: { preset: 'zoom-in', duration: 500 },
    },
    { key: 'cta', name: SN.cta, duration: 2400 },
  ],
  build(b) {
    const C = {
      peach: '#FFE7D1',
      lilac: '#E9E4FF',
      butter: '#FFF1B8',
      purple: '#5B3DF5',
      orange: '#FF7A1A',
      ink: '#1D1442',
      white: '#FFFFFF',
    };
    const F = b.F;
    const round: Partial<TextLayer> = { fontFamily: 'Nunito', fontWeight: 900 };

    const s0 = b.sc('title');
    b.backdrop(s0, 'title_bg', solid(C.peach));
    const ph = b.pick({
      tall: F.w * 0.62,
      portrait: F.h * 0.36,
      square: F.h * 0.44,
      wide: F.h * 0.62,
    });
    const pc = b.upright
      ? { x: b.cx, y: F.y + F.h * 0.25 }
      : { x: F.x + F.w * (b.wide ? 0.25 : 0.27), y: b.cy };
    b.shape(
      s0,
      'title_ring',
      b.centered(pc.x, pc.y, ph * 1.14, ph * 1.14),
      { shape: 'ellipse', fill: solid(C.purple) },
      { enter: step('pop', 0, 500, 'out-back') },
    );
    b.photo(
      s0,
      'title_photo',
      'puppy',
      b.centered(pc.x, pc.y, ph, ph),
      { borderRadius: ph },
      { enter: step('pop', 120, 500, 'out-back') },
    );
    const tt = b.upright ? b.in(0, 0.52, 1, 0.48) : b.in(0.52, 0.12, 0.48, 0.76);
    b.text(
      s0,
      'title',
      t('TOP 3', 'ТОП-3', 'ТОП-3', 'TOP 3', 'TOP 3', 'TOP 3'),
      { x: tt.x, y: tt.y, w: tt.w, h: tt.h * 0.5 },
      { ...round, fontSize: b.s(0.26), color: C.purple, lineHeight: 1 },
      { enter: step('letters-pop', 300, 450), split: { by: 'char', stagger: 70 } },
    );
    b.text(
      s0,
      'subtitle',
      t(
        'picks for happy pets',
        'речі для щасливих улюбленців',
        'вещи для счастливых питомцев',
        'hity dla szczęśliwych pupili',
        'alegeri pentru animăluțe fericite',
        'favoritos para mascotas felices',
      ),
      { x: tt.x, y: tt.y + tt.h * 0.5, w: tt.w, h: tt.h * 0.3 },
      { ...round, fontWeight: 800, fontSize: b.s(0.07), color: C.ink },
      { enter: step('words-rise', 700, 500), split: { by: 'word', stagger: 80 } },
    );
    const paws = [
      { x: 0.02, y: 0.02, r: -18 },
      { x: 0.84, y: 0.06, r: 14 },
      { x: 0.88, y: 0.88, r: -10 },
    ];
    paws.forEach((p, i) =>
      b.shape(
        s0,
        `paw_${i + 1}`,
        { ...b.in(p.x, p.y, 0, 0), w: b.s(0.09), h: b.s(0.09) },
        { shape: 'icon', icon: 'paw-print', fill: solid(C.orange), rotation: p.r },
        {
          enter: step('pop', 500 + i * 120, 400, 'out-back'),
          emphasis: emph('float', 900, 2400, 0),
        },
      ),
    );

    const items: Array<{
      key: string;
      n: number;
      photo: PhotoId;
      bg: string;
      name: Tx;
      desc: Tx;
      price: Tx;
    }> = [
      {
        key: 'n3',
        n: 3,
        photo: 'cat',
        bg: C.peach,
        name: t(
          'Cloud cat bed',
          'Лежанка «Хмаринка»',
          'Лежанка «Облачко»',
          'Legowisko Chmurka',
          'Pat Norișor',
          'Cama Nube',
        ),
        desc: t(
          'Washable, extra soft',
          'Пухка, можна прати',
          'Мягкая, можно стирать',
          'Miękkie, do prania',
          'Moale, lavabil',
          'Suave y lavable',
        ),
        price: money({ usd: 39, uah: 1199, rub: 2490, pln: 129, ron: 159, eur: 35 }),
      },
      {
        key: 'n2',
        n: 2,
        photo: 'happy-dog',
        bg: C.lilac,
        name: t(
          'Fetch ball launcher',
          'Метальник м’ячів',
          'Метатель мячей',
          'Wyrzutnia piłek',
          'Lansator de mingi',
          'Lanzapelotas',
        ),
        desc: t(
          '3 distances, auto mode',
          '3 дистанції, автомат',
          '3 дистанции, авторежим',
          '3 odległości, tryb auto',
          '3 distanțe, mod automat',
          '3 distancias, modo auto',
        ),
        price: money({ usd: 59, uah: 1899, rub: 3990, pln: 199, ron: 249, eur: 55 }),
      },
      {
        key: 'n1',
        n: 1,
        photo: 'puppy',
        bg: C.butter,
        name: t(
          'Smart feeder',
          'Розумна годівниця',
          'Умная кормушка',
          'Inteligentny karmnik',
          'Hrănitor smart',
          'Comedero inteligente',
        ),
        desc: t(
          'Meals on schedule from your phone',
          'Годування за розкладом з телефона',
          'Кормление по расписанию с телефона',
          'Posiłki według planu z telefonu',
          'Mese programate din telefon',
          'Comidas programadas desde el móvil',
        ),
        price: money({ usd: 79, uah: 2499, rub: 5490, pln: 279, ron: 349, eur: 75 }),
      },
    ];
    for (const it of items) {
      const sc = b.sc(it.key);
      b.backdrop(sc, `${it.key}_bg`, solid(it.bg));
      const num = b.pick<Box>({
        upright: b.in(0, 0, 0.42, 0.24),
        square: b.in(0, 0, 0.3, 0.3),
        wide: b.in(0, 0, 0.22, 0.4),
      });
      const card = b.pick<Box>({
        tall: b.in(0, 0.25, 1, 0.47),
        portrait: b.in(0.08, 0.24, 0.84, 0.46),
        square: b.in(0.32, 0, 0.68, 0.62),
        wide: b.in(0.5, 0, 0.5, 1),
      });
      const info = b.pick<Box>({
        upright: b.in(0, 0.74, 1, 0.26),
        square: b.in(0, 0.66, 1, 0.34),
        wide: b.in(0, 0.42, 0.46, 0.58),
      });
      const align = b.wide ? 'left' : 'center';
      b.text(
        sc,
        `number_${it.n}`,
        `#${it.n}`,
        num,
        {
          ...round,
          fontSize: b.s(0.3),
          color: it.n === 1 ? C.orange : C.purple,
          align: 'left',
          lineHeight: 1,
        },
        { enter: step('drop', 0, 700, 'bounce') },
      );
      if (it.n === 1) {
        const cw = b.s(0.1);
        b.icon(
          sc,
          'crown',
          'crown',
          { x: num.x + num.w - cw * 0.6, y: num.y, w: cw, h: cw },
          C.orange,
          { enter: step('drop', 500, 600, 'bounce'), emphasis: emph('wiggle', 1300, 800) },
        );
      }
      b.photo(
        sc,
        `photo_${it.n}`,
        it.photo,
        card,
        {
          borderRadius: b.s(0.05),
          borderWidth: b.s(0.012),
          borderColor: C.white,
          shadow: { x: 0, y: b.s(0.02), blur: b.s(0.05), color: '#1D144233' },
        },
        { enter: step('pop', 150, 550, 'out-back'), keys: b.kenBurns(1, 1.05) },
      );
      b.text(
        sc,
        `name_${it.n}`,
        it.name,
        { x: info.x, y: info.y, w: info.w, h: info.h * 0.36 },
        { ...round, fontSize: b.s(0.075), color: C.ink, align },
        { enter: step('rise', 400, 450, 'out-expo') },
      );
      b.text(
        sc,
        `desc_${it.n}`,
        it.desc,
        { x: info.x, y: info.y + info.h * 0.36, w: info.w, h: info.h * 0.24 },
        {
          fontFamily: 'Nunito',
          fontWeight: 700,
          fontSize: b.s(0.045),
          color: C.ink,
          opacity: 0.75,
          align,
        },
        { enter: step('fade', 600, 400) },
      );
      b.chip(
        sc,
        `price_${it.n}`,
        it.price,
        {
          x: b.wide ? info.x : b.cx,
          y: info.y + info.h * 0.64,
          h: b.s(0.1),
          align: b.wide ? 'left' : 'center',
        },
        { bg: C.orange, color: C.white, font: 'Nunito', weight: 900, k: 0.58 },
        { enter: step('pop', 800, 400, 'spring-bouncy'), emphasis: emph('tada', 1500, 800) },
      );
      if (it.n === 1)
        b.addFx(sc, 'confetti', 500, 2200, {
          color: C.purple,
          color2: C.orange,
          color3: '#FFFFFF',
          count: 80,
        });
    }

    const cta = b.sc('cta');
    b.backdrop(cta, 'cta_bg', solid(C.purple));
    const pw = b.s(0.13);
    b.icon(
      cta,
      'cta_paw',
      'paw-print',
      b.centered(b.cx, F.y + F.h * (b.tall ? 0.22 : 0.15), pw, pw),
      C.white,
      {
        enter: step('pop', 0, 450, 'out-back'),
        emphasis: emph('wiggle', 700, 800),
      },
    );
    b.text(
      cta,
      'cta_headline',
      t(
        'Shop all pet picks',
        'Усі хіти для улюбленців',
        'Все хиты для питомцев',
        'Wszystkie hity dla pupili',
        'Toate favoritele pentru animale',
        'Todos los favoritos para mascotas',
      ),
      b.in(0, b.tall ? 0.32 : 0.26, 1, 0.26),
      { ...round, fontSize: b.s(0.1), color: C.white },
      { enter: step('words-rise', 150, 500), split: { by: 'word', stagger: 80 } },
    );
    b.chip(
      cta,
      'cta',
      t('Shop now', 'До покупок', 'За покупками', 'Do sklepu', 'Cumpără acum', 'Comprar ya'),
      { x: b.cx, y: F.y + F.h * (b.tall ? 0.62 : 0.6), h: b.s(0.11), align: 'center' },
      { bg: C.orange, color: C.white, font: 'Nunito', weight: 900 },
      { enter: step('pop', 600, 450, 'out-back'), emphasis: emph('heartbeat', 1200, 900, 0, 300) },
    );
    b.text(
      cta,
      'site',
      'happypaws.shop',
      b.in(0, b.tall ? 0.78 : 0.8, 1, 0.08),
      { fontFamily: 'Nunito', fontWeight: 700, fontSize: b.s(0.042), color: '#FFFFFFCC' },
      { enter: step('fade', 900, 400) },
    );
    return solid(C.peach);
  },
};

/** Fashion lookbook: photo collage, split screens and whip pans between looks. */
const lookbook: VideoTemplateDef = {
  id: 'lookbook-split',
  category: 'fashion',
  format: 'portrait',
  name: t('Lookbook', 'Лукбук', 'Лукбук', 'Lookbook', 'Lookbook', 'Lookbook'),
  description: t(
    'Collage opener, split-screen looks and whip pans',
    'Колаж на старті, спліт-екрани образів і віп-пани',
    'Коллаж в начале, сплит-экраны образов и вип-паны',
    'Kolaż na start, podzielone ekrany i szybkie panoramy',
    'Colaj la început, ecrane împărțite și panoramări rapide',
    'Collage inicial, pantalla dividida y barridos rápidos',
  ),
  scenes: [
    {
      key: 'cover',
      name: t('Cover', 'Обкладинка', 'Обложка', 'Okładka', 'Copertă', 'Portada'),
      duration: 2800,
      transition: { preset: 'whip-pan', duration: 450, params: { direction: 'left' } },
    },
    {
      key: 'look1',
      name: t('Look 1', 'Образ 1', 'Образ 1', 'Stylizacja 1', 'Ținuta 1', 'Look 1'),
      duration: 2300,
      transition: { preset: 'whip-pan', duration: 450, params: { direction: 'left' } },
    },
    {
      key: 'look2',
      name: t('Look 2', 'Образ 2', 'Образ 2', 'Stylizacja 2', 'Ținuta 2', 'Look 2'),
      duration: 2300,
      transition: { preset: 'whip-pan', duration: 450, params: { direction: 'left' } },
    },
    {
      key: 'look3',
      name: t('Look 3', 'Образ 3', 'Образ 3', 'Stylizacja 3', 'Ținuta 3', 'Look 3'),
      duration: 2300,
      transition: { preset: 'zoom-out', duration: 500 },
    },
    {
      key: 'grid',
      name: t('Collage', 'Колаж', 'Коллаж', 'Kolaż', 'Colaj', 'Collage'),
      duration: 2800,
      transition: { preset: 'crossfade', duration: 600 },
    },
    { key: 'cta', name: SN.cta, duration: 2600 },
  ],
  build(b) {
    const C = { cream: '#F3EEE7', ink: '#141414', camel: '#B08D57', white: '#FFFFFF' };
    const F = b.F;
    const serif: Partial<TextLayer> = {
      fontFamily: 'Playfair Display',
      fontWeight: 500,
      fontStyle: 'italic',
      lineHeight: 1,
    };
    const caps = (size: number, color: string): Partial<TextLayer> => ({
      fontFamily: 'Inter',
      fontWeight: 600,
      fontSize: size,
      color,
      textTransform: 'uppercase',
      letterSpacing: Math.round(size * 0.3),
    });
    const g = b.s(0.018);

    // Cover: three photos as a collage, the title on a cream band
    const s0 = b.sc('cover');
    b.backdrop(s0, 'cover_bg', solid(C.cream));
    const collage: Box = b.wide
      ? { x: b.W * 0.42, y: 0, w: b.W * 0.58, h: b.H }
      : { x: 0, y: 0, w: b.W, h: b.tall ? F.y + F.h * 0.7 : b.H * 0.7 };
    const big: Box = {
      x: collage.x + g,
      y: collage.y + g,
      w: collage.w * 0.58 - g * 1.5,
      h: collage.h - g * 2,
    };
    const sx = big.x + big.w + g;
    const sw = collage.x + collage.w - g - sx;
    const sh = (collage.h - g * 3) / 2;
    const covers: Array<{ p: PhotoId; box: Box }> = [
      { p: 'fashion-model', box: big },
      { p: 'retro-style', box: { x: sx, y: collage.y + g, w: sw, h: sh } },
      { p: 'glam-portrait', box: { x: sx, y: collage.y + g * 2 + sh, w: sw, h: sh } },
    ];
    covers.forEach((c, i) =>
      b.photo(
        s0,
        `cover_photo_${i + 1}`,
        c.p,
        c.box,
        {},
        {
          enter: step('wipe', i * 160, 700, 'in-out-quart', { direction: i === 0 ? 'up' : 'left' }),
          keys: b.kenBurns(1, 1.06),
        },
      ),
    );
    // Vertical videos: the band under the text frame gets a photo
    const strip: Box = { x: 0, y: F.y + F.h + g, w: b.W, h: b.H - (F.y + F.h + g) };
    if (b.tall)
      b.photo(
        s0,
        'cover_strip',
        'leather-shoes',
        strip,
        {},
        { enter: step('wipe', 450, 700, 'in-out-quart', { direction: 'right' }) },
      );
    const tb = b.wide ? b.in(0, 0.25, 0.36, 0.5) : b.in(0, b.tall ? 0.73 : 0.74, 1, 0.26);
    b.text(
      s0,
      'title',
      'Lookbook',
      { x: tb.x, y: tb.y, w: tb.w, h: tb.h * 0.62 },
      { ...serif, fontSize: b.s(0.15), color: C.ink, align: b.wide ? 'left' : 'center' },
      {
        enter: step('letters-blur', 400, 700),
        split: { by: 'char', stagger: 40, order: 'center' },
      },
    );
    b.text(
      s0,
      'season',
      t(
        'SS26 collection',
        'Колекція SS26',
        'Коллекция SS26',
        'Kolekcja SS26',
        'Colecția SS26',
        'Colección SS26',
      ),
      { x: tb.x, y: tb.y + tb.h * 0.64, w: tb.w, h: tb.h * 0.22 },
      { ...caps(b.s(0.032), C.camel), align: b.wide ? 'left' : 'center' },
      { enter: step('fade', 900, 500) },
    );

    const looks: Array<{
      key: string;
      n: string;
      p: PhotoId;
      detail: PhotoId;
      name: Tx;
      price: Tx;
      side: 'left' | 'right' | 'full';
    }> = [
      {
        key: 'look1',
        n: '01',
        p: 'fashion-model',
        detail: 'leather-shoes',
        name: t(
          'Linen essentials',
          'Лляна база',
          'Льняная база',
          'Lniane basics',
          'Esențiale din in',
          'Básicos de lino',
        ),
        price: money({ usd: 129, uah: 4900, rub: 9900, pln: 499, ron: 599, eur: 119 }),
        side: 'left',
      },
      {
        key: 'look2',
        n: '02',
        p: 'retro-style',
        detail: 'yellow-car',
        name: t(
          'Retro weekend',
          'Ретро-вікенд',
          'Ретро-уикенд',
          'Retro weekend',
          'Weekend retro',
          'Fin de semana retro',
        ),
        price: money({ usd: 149, uah: 5600, rub: 11900, pln: 579, ron: 699, eur: 139 }),
        side: 'right',
      },
      {
        key: 'look3',
        n: '03',
        p: 'glam-portrait',
        detail: 'lipstick',
        name: t(
          'Evening red',
          'Вечірній червоний',
          'Вечерний красный',
          'Wieczorowa czerwień',
          'Roșu de seară',
          'Rojo de noche',
        ),
        price: money({ usd: 189, uah: 7200, rub: 14900, pln: 749, ron: 899, eur: 179 }),
        side: 'full',
      },
    ];
    for (const look of looks) {
      const sc = b.sc(look.key);
      b.backdrop(sc, `${look.key}_bg`, solid(C.cream));
      const full = look.side === 'full';
      let photo: Box;
      let text: Box;
      if (full) {
        photo = b.full();
        text = b.upright ? b.in(0, 0.66, 1, 0.34) : b.in(0, 0.6, 0.6, 0.4);
      } else if (b.upright) {
        const ph = b.tall ? F.y + F.h * 0.64 : b.H * 0.64;
        photo = { x: 0, y: 0, w: b.W, h: ph };
        text = { x: F.x, y: ph + g * 2, w: F.w, h: F.y + F.h - ph - g * 2 };
      } else {
        const left = look.side === 'left';
        photo = { x: left ? 0 : b.W / 2, y: 0, w: b.W / 2, h: b.H };
        const tx = left ? b.W / 2 + F.x : F.x;
        text = { x: tx, y: F.y + F.h * 0.25, w: b.W / 2 - F.x * 2, h: F.h * 0.5 };
      }
      b.photo(sc, `look_photo_${look.n}`, look.p, photo, full ? { brightness: 0.85 } : {}, {
        keys: b.kenBurns(1.02, 1.1, look.side === 'right' ? -0.01 : 0.01),
      });
      if (b.tall && !full)
        b.photo(
          sc,
          `look_detail_${look.n}`,
          look.detail,
          strip,
          {},
          {
            enter: step('wipe', 300, 600, 'in-out-quart', { direction: 'left' }),
          },
        );
      if (full)
        b.shade(sc, `look_shade_${look.n}`, b.full(), [
          [0, '#14141400'],
          [0.5, '#14141400'],
          [1, '#141414CC'],
        ]);
      const ink = full ? C.white : C.ink;
      const align = full || !b.upright ? 'left' : 'center';
      b.text(
        sc,
        `look_number_${look.n}`,
        `Look ${look.n}`,
        { x: text.x, y: text.y, w: text.w, h: text.h * 0.2 },
        { ...caps(b.s(0.03), full ? C.cream : C.camel), align },
        { enter: step('fade', 150, 400) },
      );
      b.text(
        sc,
        `look_name_${look.n}`,
        look.name,
        { x: text.x, y: text.y + text.h * 0.2, w: text.w, h: text.h * 0.48 },
        { ...serif, fontSize: b.s(0.1), color: ink, align },
        { enter: step('lines-mask', 250, 650), split: { by: 'line', stagger: 120 } },
      );
      b.text(
        sc,
        `look_price_${look.n}`,
        look.price,
        { x: text.x, y: text.y + text.h * 0.72, w: text.w, h: text.h * 0.2 },
        { fontFamily: 'Inter', fontWeight: 600, fontSize: b.s(0.04), color: ink, align },
        { enter: step('rise', 600, 450, 'out-expo') },
      );
    }

    const s4 = b.sc('grid');
    b.backdrop(s4, 'grid_bg', solid(C.cream));
    const gw = (b.W - g * 3) / 2;
    const gh = (b.H - g * 3) / 2;
    const grid: PhotoId[] = ['fashion-selfie', 'leather-shoes', 'glam-portrait', 'retro-style'];
    grid.forEach((p, i) =>
      b.photo(
        s4,
        `grid_photo_${i + 1}`,
        p,
        { x: g + (i % 2) * (gw + g), y: g + Math.floor(i / 2) * (gh + g), w: gw, h: gh },
        {},
        {
          enter: step('iris', i * 130, 650, 'out-quart'),
          keys: b.kenBurns(1.08, 1),
        },
      ),
    );
    b.chip(
      s4,
      'grid_label',
      t(
        'New in store',
        'Вже в магазині',
        'Уже в магазине',
        'Nowość w sklepie',
        'Nou în magazin',
        'Novedad en tienda',
      ),
      { x: b.W / 2, y: b.H / 2 - b.s(0.05), h: b.s(0.1), align: 'center' },
      {
        bg: C.ink,
        color: C.cream,
        font: 'Inter',
        weight: 600,
        upper: true,
        extra: { letterSpacing: b.s(0.008) },
      },
      { enter: step('pop', 700, 450, 'out-back') },
    );

    const s5 = b.sc('cta');
    b.backdrop(s5, 'cta_bg', solid(C.cream));
    const th = b.tall ? Math.min(b.s(0.36), F.w * 0.4) : Math.min(b.s(0.26), F.w * 0.28);
    const tw = th * 0.75;
    (['fashion-model', 'retro-style', 'glam-portrait'] as PhotoId[]).forEach((p, i) =>
      b.photo(
        s5,
        `cta_thumb_${i + 1}`,
        p,
        {
          x: b.cx + (i - 1) * (tw + g) - tw / 2,
          y: F.y + F.h * (b.tall ? 0.12 : 0.06),
          w: tw,
          h: th,
        },
        {},
        { enter: step('rise', i * 120, 600, 'out-expo') },
      ),
    );
    b.text(
      s5,
      'cta_headline',
      t(
        'Shop the lookbook',
        'Обирай свій образ',
        'Выбери свой образ',
        'Wybierz swój look',
        'Alege-ți ținuta',
        'Elige tu look',
      ),
      b.in(0, b.tall ? 0.4 : 0.38, 1, 0.22),
      { ...serif, fontSize: b.s(0.11), color: C.ink },
      { enter: step('lines-mask', 300, 650), split: { by: 'line', stagger: 120 } },
    );
    b.chip(
      s5,
      'cta',
      t('Shop now', 'Купити', 'Купить', 'Kup teraz', 'Cumpără', 'Comprar'),
      { x: b.cx, y: F.y + F.h * (b.tall ? 0.66 : 0.66), h: b.s(0.1), align: 'center' },
      {
        bg: C.ink,
        color: C.cream,
        font: 'Inter',
        weight: 600,
        upper: true,
        extra: { letterSpacing: b.s(0.006) },
      },
      { enter: step('fade', 800, 500) },
    );
    b.text(
      s5,
      'site',
      'atelier.store',
      b.in(0, b.tall ? 0.8 : 0.82, 1, 0.07),
      { ...caps(b.s(0.03), C.camel) },
      { enter: step('fade', 1000, 400) },
    );
    return solid(C.cream);
  },
};

export const TEMPLATES_A: VideoTemplateDef[] = [
  beatCut,
  kineticSale,
  ugcReview,
  beforeAfter,
  top3,
  lookbook,
];
